import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TimeService } from './time.service';
import { finalize, Observable, Subject, tap } from 'rxjs';
import { DisplayOptions } from '../../core/display-options';
import { DisplayOptionsService } from '../../core/display-options.service';
import { SessionData } from '../../core/session-data';
import { SessionService } from '../../core/session.service';
import { CarDetail, CheckPriceData } from './car-detail';
import { FeeOverrideRequest, FeeOverrideResponse } from './fee-override';
import { ReservationDetail } from './reservation-detail';
import { SearchCriteria, Location } from './search-types';

@Injectable({
  providedIn: 'root'
})
export class SharedSearchResultsService {

  public displayOptions: DisplayOptions;
  public currencyCode: string;

  constructor(
    private httpClient: HttpClient,
    private timeService: TimeService,
    private sessionService: SessionService,
    private displayOptionsService: DisplayOptionsService

  ) {
    this.sessionData = this.sessionService.getSessionData();
    this.displayOptions = this.displayOptionsService.getOptions();
    this.currencyCode = this.displayOptions.currency_code || 'USD';
    this.init();
  }

  private sessionData: SessionData;
  public searchPoint: any;
  public destinationSearch: any;

  private searchStarted: Subject<void>;
  private searchFinished: Subject<void>;
  private searchCriteriaSubject: Subject<SearchCriteria>;

  private searchResults: CarDetail[];
  private _searchCriteria: SearchCriteria;
  private selectedCarDetail: CarDetail;
  private price: CheckPriceData;
  private confirmation: any;

  private pickUpLocation: Location;
  private dropOffLocation: Location;

  private pickUpDate: string;
  private pickUpTime: string;
  private dropOffDate: string;
  private dropOffTime: string;

  private redirectParams: any;

  get searchCriteria(): SearchCriteria {
    return this._searchCriteria;
  }

  set searchCriteria(criteria: SearchCriteria) {
    this._searchCriteria = Object.assign(new SearchCriteria(), criteria);
    this.searchCriteriaSubject.next(this._searchCriteria);
    sessionStorage.setItem("SearchCriteria", JSON.stringify(this._searchCriteria));
  }

  init() {
    this.searchStarted = new Subject<any>();
    this.searchFinished = new Subject<any>();
    this.searchCriteriaSubject = new Subject<SearchCriteria>();
    this.redirectParams = {};
    this.searchResults = [];

    var sc = JSON.parse(sessionStorage.getItem("SearchCriteria")) as SearchCriteria;
    if (sc) this.searchCriteria = sc;
    this.selectedCarDetail = JSON.parse(sessionStorage.getItem("SelectedCar1")) as CarDetail;
    this.price = JSON.parse(sessionStorage.getItem("SelectedCarPrice")) as CheckPriceData;
  }

  public getResults(): CarDetail[] {
    return this.searchResults;
  }

  public hasResults(): boolean {
    return this.searchResults.length > 0;
  }

  public getCarDetail(): CarDetail {
    return this.selectedCarDetail;
  }

  public setCarDetail(detail: CarDetail) {
    this.selectedCarDetail = Object.assign({}, detail);
    sessionStorage.setItem("SelectedCar1", JSON.stringify(this.selectedCarDetail));
  }

  public getPrice(): CheckPriceData {
    return this.price;
  }

  public setPrice(price: CheckPriceData): void {
    this.price = Object.assign({}, price);
    sessionStorage.setItem("SelectedCarPrice", JSON.stringify(this.price));
  }

  public get SearchStarted(): Observable<any> {
    return this.searchStarted.asObservable();
  }

  public get SearchFinished(): Observable<any> {
    return this.searchFinished.asObservable();
  }

  public get SearchCriteria(): Observable<SearchCriteria> {
    return this.searchCriteriaSubject.asObservable();
  }

  public setConfirmation(confirmationParam: any): void {
    this.confirmation = confirmationParam;
  }

  public getConfirmation(): any {
    return this.confirmation;
  }

  public search(sc: SearchCriteria): Observable<any> {

    this.searchResults = [];

    this.searchStarted.next();

    this.pickUpLocation = sc.pickUp.location;
    this.dropOffLocation = sc.dropOff.location;

    this.pickUpDate = this.timeService.formatDate(sc.pickUp.date);
    this.pickUpTime = this.timeService.formatTime(sc.pickUp.time);

    this.dropOffDate = this.timeService.formatDate(sc.dropOff.date);
    this.dropOffTime = this.timeService.formatTime(sc.dropOff.time);

    return this.searchForCars()
      .pipe(
        tap(response => {
          this.searchResults = response.offers;
          this.searchPoint = response.searchPointCoordinate;
          this.destinationSearch = response.destinationSearch;
          if (this.displayOptions.options && this.displayOptions.options.car_combined_store) {
            this.searchResults.forEach(subType => {
              if (subType.pointsSubType === "" || subType.pointsSubType === null) {
                subType.pointsSubType = "noPoints"
              }
            })
          }
        }),
        finalize(() => {
          // inform subscribers about new search result
          this.searchFinished.next();
        }));
  }

  public setCache(name: string, params: any) {
    this.redirectParams[name] = params;
  }

  public getCache(name: string, remove: boolean): any {

    if (this.redirectParams.hasOwnProperty(name)) {
      const val = this.redirectParams[name];
      if (remove) {
        delete this.redirectParams[name];
      }

      return val;
    }

    return undefined;
  }

  public getReservation(confirmationId: String): Observable<ReservationDetail> {
    return this.httpClient
      .get<ReservationDetail>(this.sessionData.apiUrl + `/reservation/${confirmationId}`);
  }

  public checkAppliedFeeOverride(feeOverrideRequest: FeeOverrideRequest):
    Observable<FeeOverrideResponse> {
    return this.httpClient
      .post<FeeOverrideResponse>(
        this.sessionData.apiUrl + '/reservation/feeoverride',
        feeOverrideRequest);
  }

  public refreshSearch() {
    if (this.pickUpLocation && this.dropOffLocation) {
      return this.searchForCars();
    }

    return undefined;
  }

  private searchForCars() {

    let params = {
      'CorrelationId': this.sessionData.correlationId,
      'PickUpDate': this.pickUpDate,
      'PickUpTime': this.pickUpTime,
      'ReturnDate': this.dropOffDate,
      'ReturnTime': this.dropOffTime,
      'CurrencyCode': this.currencyCode,
    };

    if (this.pickUpLocation.hasAddress()) {
      params = Object.assign(params, {
        'PickUpLocationAddress': this.pickUpLocation.locationName,
        'ReturnLocationAddress': this.pickUpLocation.locationName
      });

    } else {
      params = Object.assign(params, {
        'PickUpLocationId': this.pickUpLocation.locationId,
        'ReturnLocationId': this.dropOffLocation && this.dropOffLocation.locationId || this.pickUpLocation.locationId
      });
    }

    return this.httpClient
      .get<any>(this.sessionData.apiUrl + '/search', { params: params });
  }
}
